import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signupApi, setAuthToken, setTenantSlug, isAppsHost } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

type Step = 'org' | 'admin' | 'otp' | 'success';

function CopyableError({ error }: { error: string }) {
  const [copied, setCopied] = useState(false);
  if (!error) return null;
  const handleCopy = () => {
    navigator.clipboard.writeText(error).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <span className="break-all">{error}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="shrink-0 text-xs text-red-400 hover:text-red-600 border border-red-200 rounded px-1.5 py-0.5"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 30);
}

export function SignUpPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  // Step state
  const [step, setStep] = useState<Step>('org');

  // Referral code from ?ref= URL param (preserved across steps via sessionStorage)
  const [referredBy] = useState<string>(() => {
    const urlRef = new URLSearchParams(window.location.search).get('ref');
    if (urlRef) {
      sessionStorage.setItem('signup_ref', urlRef);
      return urlRef;
    }
    return sessionStorage.getItem('signup_ref') || '';
  });

  // Form fields
  const [orgName, setOrgName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [otp, setOtp] = useState('');

  // Slug availability
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugReason, setSlugReason] = useState('');
  const [checkingSlug, setCheckingSlug] = useState(false);

  // UI state
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [provisioningMessage, setProvisioningMessage] = useState('');

  // Result
  const [resultSlug, setResultSlug] = useState('');
  const [resultOrgName, setResultOrgName] = useState('');
  const [resultToken, setResultToken] = useState('');

  // Redirect if already authenticated
  if (isAuthenticated) {
    if (isAppsHost()) {
      // Can't go to /home on apps host — do nothing, let them complete the flow
    } else {
      navigate('/home', { replace: true });
      return null;
    }
  }

  // Warm up the Vercel function + master DB before the user starts filling the form.
  // Runs once on mount so the backend is ready by the time they hit "Send Code".
  useEffect(() => { signupApi.preflight(); }, []);

  // Auto-generate slug from org name
  useEffect(() => {
    if (!slugEdited && orgName) {
      setSlug(slugify(orgName));
    }
  }, [orgName, slugEdited]);

  // Debounced slug availability check
  const checkSlugAvailability = useCallback(async (s: string) => {
    if (s.length < 3) {
      setSlugAvailable(null);
      setSlugReason('');
      return;
    }
    setCheckingSlug(true);
    try {
      const result = await signupApi.checkSlug(s);
      setSlugAvailable(result.available);
      setSlugReason(result.reason || '');
    } catch {
      setSlugAvailable(null);
      setSlugReason('');
    } finally {
      setCheckingSlug(false);
    }
  }, []);

  useEffect(() => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      setSlugReason('');
      return;
    }
    const timer = setTimeout(() => checkSlugAvailability(slug), 400);
    return () => clearTimeout(timer);
  }, [slug, checkSlugAvailability]);

  const handleSlugChange = (value: string) => {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30);
    setSlug(cleaned);
    setSlugEdited(true);
  };

  const handleOrgStep = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!orgName.trim()) {
      setError('Organization name is required');
      return;
    }
    if (!slug || slug.length < 3) {
      setError('URL must be at least 3 characters');
      return;
    }
    if (slugAvailable === false) {
      setError(slugReason || 'This URL is not available');
      return;
    }
    setStep('admin');
  };

  const handleAdminStep = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!adminName.trim()) {
      setError('Your name is required');
      return;
    }
    if (!adminEmail.trim()) {
      setError('Email is required');
      return;
    }

    setIsLoading(true);
    try {
      await signupApi.requestOTP({
        orgName: orgName.trim(),
        slug,
        adminName: adminName.trim(),
        adminEmail: adminEmail.toLowerCase().trim(),
      });
      setStep('otp');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const timers: ReturnType<typeof setTimeout>[] = [];
    const msg = (text: string, ms: number) =>
      timers.push(setTimeout(() => setProvisioningMessage(text), ms));

    setProvisioningMessage('Verifying code...');
    msg('Checking for previous attempts...', 1200);
    msg('Creating your workspace database...', 3500);
    msg('Setting up your workspace...', 8000);
    msg('Seeding default data...', 16000);
    msg('Almost there...', 24000);

    try {
      const result = await signupApi.verifyOTP(adminEmail.toLowerCase().trim(), otp, referredBy || undefined);
      timers.forEach(clearTimeout);

      // Store token and tenant slug
      setAuthToken(result.token);
      setTenantSlug(result.slug);

      // Update auth context
      login(result.user);

      // Clear referral from sessionStorage
      sessionStorage.removeItem('signup_ref');

      setResultSlug(result.slug);
      setResultOrgName(orgName.trim());
      setResultToken(result.token);
      setStep('success');
    } catch (err: any) {
      timers.forEach(clearTimeout);
      setError(err.message || 'Verification failed');
      setProvisioningMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartOver = () => {
    setOrgName('');
    setSlug('');
    setSlugEdited(false);
    setAdminName('');
    setAdminEmail('');
    setOtp('');
    setError('');
    setProvisioningMessage('');
    setSlugAvailable(null);
    setSlugReason('');
    setStep('org');
  };

  const handleResendOTP = async () => {
    setError('');
    setIsLoading(true);
    try {
      await signupApi.requestOTP({
        orgName: orgName.trim(),
        slug,
        adminName: adminName.trim(),
        adminEmail: adminEmail.toLowerCase().trim(),
      });
      setOtp('');
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToWorkspace = () => {
    if (isAppsHost() && resultSlug && resultToken) {
      window.location.href = `https://${resultSlug}.tabillo.com/#auth_token=${resultToken}`;
    } else {
      navigate('/home', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900" style={{ letterSpacing: '-0.04em' }}>Tabillo</h1>
          <p className="mt-2 text-gray-600">Create your workspace</p>
        </div>

        {/* Progress indicator */}
        {step !== 'success' && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {(['org', 'admin', 'otp'] as const).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === s
                      ? 'bg-blue-600 text-white'
                      : ['org', 'admin', 'otp'].indexOf(step) > i
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {['org', 'admin', 'otp'].indexOf(step) > i ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                {i < 2 && <div className="w-8 h-0.5 bg-gray-200" />}
              </div>
            ))}
          </div>
        )}

        {/* Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* Step 1: Organization */}
          {step === 'org' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Name your organization
              </h2>

              <form onSubmit={handleOrgStep} className="space-y-4">
                <div>
                  <label htmlFor="orgName" className="block text-sm font-medium text-gray-700 mb-1">
                    Organization name
                  </label>
                  <input
                    id="orgName"
                    type="text"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Acme Corp"
                    required
                    autoFocus
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
                    Workspace URL
                  </label>
                  <div className="flex items-center">
                    <input
                      id="slug"
                      type="text"
                      value={slug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      placeholder="acme-corp"
                      required
                      className={`flex-1 px-3 py-2 border rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        slugAvailable === true
                          ? 'border-green-300'
                          : slugAvailable === false
                            ? 'border-red-300'
                            : 'border-gray-300'
                      }`}
                    />
                    <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-sm text-gray-500">
                      .tabillo.com
                    </span>
                  </div>
                  {/* Slug status */}
                  <div className="mt-1 h-5">
                    {checkingSlug && (
                      <p className="text-xs text-gray-500">Checking availability...</p>
                    )}
                    {!checkingSlug && slugAvailable === true && slug.length >= 3 && (
                      <p className="text-xs text-green-600">This URL is available</p>
                    )}
                    {!checkingSlug && slugAvailable === false && (
                      <p className="text-xs text-red-600">{slugReason}</p>
                    )}
                  </div>
                </div>

                {error && <CopyableError error={error} />}

                <button
                  type="submit"
                  disabled={!orgName.trim() || !slug || slug.length < 3 || slugAvailable === false || checkingSlug}
                  className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Continue
                </button>

                <p className="text-xs text-gray-400 text-center leading-relaxed">
                  By signing up, you agree to Tabillo's{' '}
                  <a href="https://www.tabillo.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">Terms of Use</a>
                  {' & '}
                  <a href="https://www.tabillo.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">Privacy Policy</a>
                  . By providing your email, you consent to receive communications from Tabillo. You may withdraw at any time by opting out.
                </p>
              </form>
            </>
          )}

          {/* Step 2: Admin Account */}
          {step === 'admin' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                Your admin account
              </h2>
              <p className="text-sm text-gray-600 mb-6 text-center">
                You'll be the administrator of <strong>{orgName}</strong>
              </p>

              <form onSubmit={handleAdminStep} className="space-y-4">
                <div>
                  <label htmlFor="adminName" className="block text-sm font-medium text-gray-700 mb-1">
                    Your name
                  </label>
                  <input
                    id="adminName"
                    type="text"
                    value={adminName}
                    onChange={(e) => setAdminName(e.target.value)}
                    placeholder="Jane Smith"
                    required
                    autoFocus
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700 mb-1">
                    Email address
                  </label>
                  <input
                    id="adminEmail"
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="jane@acme.com"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {error && <CopyableError error={error} />}

                <button
                  type="submit"
                  disabled={isLoading || !adminName.trim() || !adminEmail.trim()}
                  className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Sending code...' : 'Send Verification Code'}
                </button>

                <button
                  type="button"
                  onClick={() => { setStep('org'); setError(''); }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  Back
                </button>
              </form>
            </>
          )}

          {/* Step 3: OTP Verification */}
          {step === 'otp' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                Verify your email
              </h2>
              <p className="text-sm text-gray-600 mb-6 text-center">
                We sent a 6-digit code to<br />
                <span className="font-medium text-gray-900">{adminEmail}</span>
              </p>

              <form onSubmit={handleOTPSubmit} className="space-y-4">
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                    Verification code
                  </label>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    required
                    autoFocus
                    maxLength={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest font-mono"
                  />
                </div>

                {provisioningMessage && isLoading && (
                  <div className="text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-blue-600" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {provisioningMessage}
                  </div>
                )}

                {error && error === 'instance_creation_failed' ? (
                  <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    Instance creation failed.{' '}
                    <button
                      type="button"
                      onClick={handleStartOver}
                      className="font-medium underline hover:text-red-900"
                    >
                      Click here
                    </button>
                    {' '}to try again.
                  </div>
                ) : error ? (
                  <CopyableError error={error} />
                ) : null}

                <button
                  type="submit"
                  disabled={isLoading || otp.length !== 6}
                  className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Please wait...' : 'Verify & Create Workspace'}
                </button>
              </form>

              <div className="mt-4 flex items-center justify-center gap-4 text-sm">
                <button
                  onClick={handleResendOTP}
                  disabled={isLoading}
                  className="text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
                >
                  Resend code
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => { setStep('admin'); setOtp(''); setError(''); setProvisioningMessage(''); }}
                  disabled={isLoading}
                  className="text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
                >
                  Change email
                </button>
              </div>
            </>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Your workspace is ready!
              </h2>
              <p className="text-sm text-gray-600 mb-1">
                <strong>{resultOrgName}</strong>
              </p>
              <p className="text-xs text-gray-500 mb-6">
                {resultSlug}.tabillo.com
              </p>

              <button
                onClick={handleGoToWorkspace}
                className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Go to your workspace
              </button>
            </div>
          )}
        </div>

        {/* Footer link */}
        {step !== 'success' && (
          <p className="mt-6 text-sm text-gray-500 text-center">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 hover:underline">
              Sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
